import { GraphQLRoutingRequester, type InputActivity, type NewRoute, type ParsedSpreadsheetTab } from '@cobliteam/api-lib';
import type { UUID } from '@cobliteam/common-types';
import { i18nConfig } from '@cobliteam/i18n';
import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { toast } from 'sonner';
import { getDataFromSpreadsheet } from '../../utils';
import {
  deleteRoutesFailed,
  deleteRoutesSucceeded,
  editRouteFailed,
  editRouteSucceeded,
  importActivitiesSpreadsheetFailed,
  importActivitiesSpreadsheetSucceeded,
  parseSpreadsheetFailed,
  parseSpreadsheetSucceeded,
  routingFailed,
  routingSucceeded,
} from './actionCreators';
import type { DeleteRoutesRequest, EditRouteRequest, ImportActivitiesSpreadsheetRequest, ParseSpreadsheetRequest, RoutingRequest } from './actions';
import { ActionTypes } from './actionTypes';
import { parseRoutingErrors, parserRouteEditingErrors, parseSpreadsheetErrors } from './errors';

export const graphQLRoutingRequester: GraphQLRoutingRequester = new GraphQLRoutingRequester();

export const defaultAlertConfig = { hasCloseBtn: true };

export const spreadsheetAlertConfig = { ttl: 6000 };

export function* requestRoutingSaga(action: RoutingRequest) {
  const routingSettings = action.payload;

  try {
    const routes: NewRoute[] = yield call(graphQLRoutingRequester.createRoutes, routingSettings, routingSettings.isAccessAllowed);
    yield put(routingSucceeded(routes));
  } catch (err) {
    // getFixedT não carrega namespace; sem isso o alerta mostraria a chave crua se `routing` não estiver carregado.
    yield call([i18nConfig, i18nConfig.loadNamespaces], 'routing');
    toast.error(parseRoutingErrors(err, i18nConfig.getFixedT(null, 'routing')));
    yield put(routingFailed());
  }
}

export function* requestEditRouteSaga(action: EditRouteRequest) {
  try {
    const { editRoute, notifyRouteEditRequestResult, isAccessAllowed } = action.payload;

    const editedRoute: NewRoute = yield call(graphQLRoutingRequester.editRoute, editRoute, isAccessAllowed);

    yield put(editRouteSucceeded(editedRoute));

    if (notifyRouteEditRequestResult === true) {
      toast.success('Rota editada com sucesso!');
    }

    if (notifyRouteEditRequestResult === false) {
      toast.success('Rota duplicada com sucesso.');
    }
  } catch (err) {
    yield put(editRouteFailed());
    yield call([i18nConfig, i18nConfig.loadNamespaces], 'routing');
    toast.error(parserRouteEditingErrors(err, i18nConfig.getFixedT(null, 'routing')));
  }
}

export function* requestDeleteRoutesSaga(action: DeleteRoutesRequest) {
  try {
    const { routesToDelete, notifyDeleteRouteRequestResult } = action.payload;
    const deletedRoutes: UUID[] = yield call(graphQLRoutingRequester.deleteRoutes, routesToDelete);
    yield put(deleteRoutesSucceeded(deletedRoutes));
    if (notifyDeleteRouteRequestResult) {
      toast.success('Rota excluída com sucesso!');
    }
  } catch (_err) {
    yield put(deleteRoutesFailed());
  }
}

export function* requestActivitiesSpreadsheetImportSaga(action: ImportActivitiesSpreadsheetRequest) {
  const formValues = action.payload;

  try {
    const spreadsheetImportResultAddresses: InputActivity[] = yield call(graphQLRoutingRequester.importSpreadsheet, formValues);
    yield put(importActivitiesSpreadsheetSucceeded(spreadsheetImportResultAddresses));
  } catch (_err) {
    toast.error('Houve um erro, tente novamente mais tarde.');
    yield put(importActivitiesSpreadsheetFailed());
  }
}

export function* parseSpreadsheetSaga(file: ParseSpreadsheetRequest) {
  try {
    const parsedSpreadsheet: ParsedSpreadsheetTab[] = yield call(getDataFromSpreadsheet, file);
    yield put(parseSpreadsheetSucceeded(parsedSpreadsheet));
  } catch (err) {
    toast.error(parseSpreadsheetErrors(err));
    yield put(parseSpreadsheetFailed());
  }
}

export function* NewRoutingSagasListener() {
  yield all([
    takeLatest(ActionTypes.ROUTING_REQUEST, requestRoutingSaga),
    takeEvery(ActionTypes.EDIT_ROUTE_REQUEST, requestEditRouteSaga),
    takeLatest(ActionTypes.DELETE_ROUTES_REQUEST, requestDeleteRoutesSaga),
    takeLatest(ActionTypes.IMPORT_ACTIVITIES_SPREADSHEET_REQUEST, requestActivitiesSpreadsheetImportSaga),
    takeLatest(ActionTypes.PARSE_SPREADSHEET_REQUEST, parseSpreadsheetSaga),
  ]);
}
